import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Components */
import { DataLayout, Icon, Pagination } from "../../components"

/* Helpers */
import { utils } from "../../helpers"

/* REST API */
import { users, send } from "../../api/Notification"

/* Widgets */
import { toast, confirm } from "../common"
import Preview from "./Preview"

/* Shared */
import { typeOf } from "./shared"

Modal.setAppElement("#root")

/* The colours the letter of a name is put on */
const PALETTE = ["#0279fc", "#e53935", "#43a047", "#8e24aa", "#00897b", "#f4511e", "#6d4c41"]


/* Widget Send Modal — the choice of the recipients. The card of the phone stands on the left
   so the admin sees what he is about to send, and the whole audience is one button away */
class SendModal extends Component {

    constructor() {
        super()

        this.state = {
            search: "",
            status: "",
            loading: false,
            data: [],
            page: 1,
            count: 0,
            selected: [],
            sending: false
        }

        this._timer = null
    }

    componentDidUpdate(previous) {
        if (!previous.visible && this.props.visible) {
            this.setState({ search: "", data: [], selected: [], status: "", page: 1 }, () => this.load())
        }
    }

    load = (page = 1) => {

        /* Fields */
        const { template } = this.props
        const { search } = this.state

        this.setState({ page, loading: true })

        users({ page, type: template.type, search }).then(response => {
            if (response && response.status === 200) {
                this.setState({ data: response.data.list, count: response.data.count, loading: false, status: "ok" })
            }
            else {
                this.setState({ loading: false, status: "error" })
            }
        })
    }

    searchText = text => {
        this.setState({ search: text, selected: [] })
        clearTimeout(this._timer)
        this._timer = setTimeout(() => this.load(1), 400)
    }

    toggle = id => {

        const { selected } = this.state
        const index = selected.indexOf(id)

        if (index > -1) {
            const next = [...selected]
            next.splice(index, 1)
            this.setState({ selected: next })
        }
        else {
            this.setState({ selected: [...selected, id] })
        }
    }

    toggleAll = () => {

        const { data, selected } = this.state
        const ids = data.map(item => item.id)

        if (ids.every(id => selected.indexOf(id) > -1)) {
            this.setState({ selected: selected.filter(id => ids.indexOf(id) === -1) })
        }
        else {
            this.setState({ selected: [...new Set([...selected, ...ids])] })
        }
    }

    /* One door for both buttons — everybody or the chosen ones */
    deliver = (all, ids) => {

        /* Fields */
        const { template, onClose, onSent } = this.props

        this.setState({ sending: true })

        send({
            type: template.type,
            title: template.title,
            message: template.description,
            button: template.button,
            buttonColor: template.buttonColor,
            screen: template.screen,
            params: template.params,
            image: template.image,
            all,
            userIds: all ? [] : ids,
            templateId: template.id
        }).then(response => {

            this.setState({ sending: false })

            if (response && response.status === 200) {
                const sent = response.data && response.data.recipients ? response.data.recipients : 0
                toast.success(sent > 0 ? `Уведомление ушло ${sent} получателям` : "Уведомление отправлено")
                onClose()
                onSent && onSent(template.id)
            }
            else {
                toast.error("Не удалось отправить уведомление")
            }
        })
    }

    sendAll = () => {

        const { template } = this.props
        const { sending } = this.state

        if (sending) {
            return
        }

        const type = typeOf(template.type)

        confirm({
            title: `Отправить всем ${type.receivers}?`,
            text: "Уведомление придёт каждому, и отменить его будет нельзя",
            confirmText: "Отправить",
            cancelText: "Отмена"
        }).then(confirmed => {
            if (confirmed) {
                this.deliver(true, [])
            }
        })
    }

    sendSelected = () => {

        const { selected, sending } = this.state

        if (sending) {
            return
        }

        if (selected.length === 0) {
            toast.error("Выберите получателей")
            return
        }

        confirm({
            title: `Отправить ${selected.length} получателям?`,
            confirmText: "Отправить",
            cancelText: "Отмена"
        }).then(confirmed => {
            if (confirmed) {
                this.deliver(false, selected)
            }
        })
    }

    /* The colour of the letter — the same name always gets the same one */
    color = name => {

        if (!name) {
            return PALETTE[0]
        }

        let code = 0

        for (let index = 0; index < name.length; index++) {
            code = name.charCodeAt(index) + ((code << 5) - code)
        }

        return PALETTE[Math.abs(code) % PALETTE.length]
    }

    /* The left side — what is being sent and the button for the whole audience */
    _side = () => {

        /* Fields */
        const { template } = this.props
        const { sending } = this.state

        const type = typeOf(template.type)

        return (
            <div className="nt-send-side">

                <div className="nt-send-side-text">Как увидит человек</div>

                <Preview
                    dark
                    title={template.title}
                    description={template.description}
                    button={template.button}
                    buttonColor={template.buttonColor}
                    image={template.image}
                />

                <div onClick={this.sendAll} className={`nt-send-all ${sending ? "disabled" : ""}`}>
                    <Icon name="notification" size={17} />
                    <span>{sending ? "Отправка..." : `Отправить всем ${type.receivers}`}</span>
                </div>

                <div className="nt-send-note">Уведомление сохранится в приложении, даже если телефон не примет пуш</div>

            </div>
        )
    }

    /* The right side — the people picked by hand */
    _main = () => {

        /* Fields */
        const { search, data, count, page, status, loading, selected, sending } = this.state

        const ids = data.map(item => item.id)
        const all = ids.length > 0 && ids.every(id => selected.indexOf(id) > -1)

        return (
            <div className="nt-send-main">

                <div className="nt-send-main-head">
                    <div className="nt-send-main-text">Выбрать вручную</div>
                    {selected.length > 0 && <div className="nt-tag blue">{selected.length} выбрано</div>}
                </div>

                <div className="nt-search">

                    <div className="nt-search-icon">
                        <Icon name="search" size={17} />
                    </div>

                    <input placeholder="Имя или телефон" value={search} onChange={event => this.searchText(event.target.value)} />

                    {search.length > 0 &&
                        <div onClick={() => this.searchText("")} className="nt-search-clear">
                            <Icon name="close" size={11} width={2.4} />
                        </div>
                    }

                </div>

                {data.length > 0 &&
                    <div onClick={this.toggleAll} className="nt-send-select-all">
                        <div className={`nt-checkbox ${all ? "checked" : ""}`}>{all && <Icon name="check" size={11} width={2.6} />}</div>
                        <span>Выбрать всех на странице</span>
                    </div>
                }

                <div className="nt-send-list">

                    {(loading || data.length === 0)
                        ? <DataLayout status={loading ? "loading" : status} reload={() => this.load(1)} data={data} />
                        : data.map((item, index) => {

                            const checked = selected.indexOf(item.id) > -1

                            return (
                                <div key={`${index}`} onClick={() => this.toggle(item.id)} className={`nt-send-user ${checked ? "checked" : ""}`}>

                                    <div className="nt-send-user-avatar" style={{ background: this.color(item.name) }}>
                                        {item.name ? item.name[0].toUpperCase() : "?"}
                                    </div>

                                    <div className="nt-send-user-title">
                                        <div className="nt-send-user-name">{utils.check(item.name, "Без имени")}</div>
                                        <div className="nt-send-user-text">{utils.check(item.phone, utils.check(item.email, "—"))}</div>
                                    </div>

                                    <div className={`nt-checkbox ${checked ? "checked" : ""}`}>{checked && <Icon name="check" size={11} width={2.6} />}</div>

                                </div>
                            )
                        })
                    }

                </div>

                {count > 10 &&
                    <div className="nt-send-pagination">
                        <Pagination size={10} page={page} total={count} onPageChange={page => this.load(page)} />
                    </div>
                }

                {selected.length > 0 &&
                    <div onClick={this.sendSelected} className={`nt-button ${sending ? "disabled" : ""}`}>
                        {sending ? "Отправка..." : `Отправить выбранным (${selected.length})`}
                    </div>
                }

            </div>
        )
    }

    render() {

        /* Fields */
        const { visible, onClose, template } = this.props

        if (!template) {
            return null
        }

        return (
            <Modal
                isOpen={visible}
                onRequestClose={onClose}
                className="nt-send-modal"
                overlayClassName="nt-details-overlay"
                closeTimeoutMS={300}
            >

                <div className="nt-send">

                    <div className="nt-form-head">

                        <div className="nt-form-head-title">
                            <div className="nt-form-name">Отправка уведомления</div>
                            <div className="nt-form-text">{utils.check(template.title, "Без заголовка")}</div>
                        </div>

                        <div onClick={onClose} className="nt-details-close">
                            <Icon name="close" size={11} width={2.2} />
                        </div>

                    </div>

                    <div className="nt-send-body">
                        {this._side()}
                        {this._main()}
                    </div>

                </div>

            </Modal>
        )
    }
}

export default SendModal
